import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Collapse,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
  Button,
  Badge,
  alpha,
} from '@mui/material';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Circle,
  Link2,
  ArrowRight,
  GitBranch,
  Layers,
  Filter,
  ChevronsDownUp,
  ChevronsUpDown,
  AlertTriangle,
  Lock,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  ArrowUpRight,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Project, Task, TaskStatus, ProjectStatus, TaskPriority, TaskCategory, IssueType } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import { formatPercent } from '@/shared/utils/formatPercentage';

// Category colors for display
const categoryColors: Record<TaskCategory, string> = {
  development: '#4F46E5',
  design: '#8b5cf6',
  testing: '#16a34a',
  documentation: '#0891b2',
  research: '#7c3aed',
  meeting: '#f59e0b',
  review: '#ec4899',
  support: '#14b8a6',
  maintenance: '#6366f1',
  deployment: '#ef4444',
  planning: '#64748b',
  other: '#9ca3af',
};

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#4F46E5', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

interface ProjectTreeViewProps {
  project: Project;
  childProjects: Project[];
  tasks: Task[];
  onTaskClick?: (taskId: string) => void;
}

interface TaskNodeProps {
  task: Task;
  allTasks: Task[];
  level: number;
  expandedNodes: Set<string>;
  onToggleExpand: (id: string) => void;
  showDependencies: boolean;
  onTaskClick?: (taskId: string) => void;
}

interface ProjectNodeProps {
  project: Project;
  childProjects: Project[];
  tasks: Task[];
  level: number;
  expandedNodes: Set<string>;
  onToggleExpand: (id: string) => void;
  showDependencies: boolean;
  onTaskClick?: (taskId: string) => void;
}

// Status colors
const getStatusColor = (status: string): string => {
  switch (status) {
    case ProjectStatus.PLANNING:
    case TaskStatus.NOT_STARTED:
      return '#64748b';
    case ProjectStatus.ACTIVE:
    case TaskStatus.IN_PROGRESS:
      return '#4F46E5';
    case ProjectStatus.ON_HOLD:
    case TaskStatus.BLOCKED:
      return '#f59e0b';
    case ProjectStatus.COMPLETED:
    case TaskStatus.COMPLETED:
      return '#16a34a';
    case ProjectStatus.CANCELLED:
    case TaskStatus.CANCELLED:
      return '#ef4444';
    default:
      return '#64748b';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  const labels: Record<TaskStatus, string> = {
    [TaskStatus.NOT_STARTED]: 'Not Started',
    [TaskStatus.IN_PROGRESS]: 'In Progress',
    [TaskStatus.BLOCKED]: 'Blocked',
    [TaskStatus.COMPLETED]: 'Completed',
    [TaskStatus.CANCELLED]: 'Cancelled',
  };
  return labels[status] || status;
};

/**
 * TaskNode
 * Renders a task with its subtasks and dependency indicators
 */
const TaskNode: React.FC<TaskNodeProps> = ({
  task,
  allTasks,
  level,
  expandedNodes,
  onToggleExpand,
  showDependencies,
  onTaskClick,
}) => {
  const navigate = useNavigate();
  const isExpanded = expandedNodes.has(task.id);

  // Get direct subtasks
  const subtasks = useMemo(() => {
    return allTasks.filter((t) => t.parentTaskId === task.id);
  }, [allTasks, task.id]);

  // Get dependency information
  const blockedByTasks = useMemo(() => {
    return (task.blockedByTaskIds || [])
      .map((id) => allTasks.find((t) => t.id === id))
      .filter(Boolean) as Task[];
  }, [task.blockedByTaskIds, allTasks]);

  const blockingTasks = useMemo(() => {
    return (task.blockingTaskIds || [])
      .map((id) => allTasks.find((t) => t.id === id))
      .filter(Boolean) as Task[];
  }, [task.blockingTaskIds, allTasks]);

  const hasSubtasks = subtasks.length > 0;
  const hasDependencies = blockedByTasks.length > 0 || blockingTasks.length > 0;
  const isOverdue = dayjs(task.dueDate).isBefore(dayjs()) && task.status !== TaskStatus.COMPLETED;
  const isBlocked = task.status === TaskStatus.BLOCKED || blockedByTasks.some(t => t.status !== TaskStatus.COMPLETED);

  // Calculate subtask progress
  const subtaskProgress = useMemo(() => {
    if (subtasks.length === 0) return null;
    const completed = subtasks.filter((t) => t.status === TaskStatus.COMPLETED).length;
    return {
      total: subtasks.length,
      completed,
      percentage: Math.round((completed / subtasks.length) * 100),
    };
  }, [subtasks]);

  const handleClick = () => {
    if (hasSubtasks) {
      onToggleExpand(task.id);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTaskClick) {
      onTaskClick(task.id);
    } else {
      navigate(`/tasks?taskId=${task.id}`);
    }
  };

  return (
    <Box>
      {/* Task Row */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          pl: level * 3 + 1.5,
          borderRadius: 1,
          cursor: hasSubtasks ? 'pointer' : 'default',
          bgcolor: isBlocked ? alpha('#f59e0b', 0.05) : 'transparent',
          borderLeft: level > 0 ? '2px solid' : 'none',
          borderColor: level > 0 ? 'divider' : 'transparent',
          ml: level > 0 ? 2 : 0,
          '&:hover': {
            bgcolor: isBlocked ? alpha('#f59e0b', 0.1) : 'action.hover',
          },
        }}
      >
        {/* Expand/Collapse Icon */}
        {hasSubtasks ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(task.id);
            }}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}

        {/* Task Icon with Level Indicator */}
        <Tooltip title={hasSubtasks ? `${subtasks.length} subtask(s)` : 'Task'}>
          <Box sx={{ position: 'relative' }}>
            {hasSubtasks ? (
              <Badge
                badgeContent={subtasks.length}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 14,
                    minWidth: 14,
                    right: -2,
                    top: -2,
                  },
                }}
              >
                <Layers size={18} style={{ color: '#8b5cf6' }} />
              </Badge>
            ) : (
              <FileText size={18} style={{ color: '#64748b' }} />
            )}
          </Box>
        </Tooltip>

        {/* Task Title */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            onClick={handleNavigate}
            sx={{
              fontWeight: hasSubtasks ? 500 : 400,
              textDecoration: task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
              color: task.status === TaskStatus.COMPLETED ? 'text.secondary' : 'text.primary',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
          >
            {task.title}
          </Typography>
          {/* Parent task link for displaying hierarchy */}
          {task.parentTaskId && level === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
              }}
              onClick={(e) => {
                e.stopPropagation();
                const parentTask = allTasks.find((t) => t.id === task.parentTaskId);
                if (parentTask) {
                  if (onTaskClick) {
                    onTaskClick(parentTask.id);
                  } else {
                    navigate(`/tasks?taskId=${parentTask.id}`);
                  }
                }
              }}
            >
              <ArrowUpRight size={10} style={{ color: '#6366f1' }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Subtask
              </Typography>
            </Box>
          )}
        </Box>

        {/* Issue Type Badge */}
        {task.issueType && issueTypeConfig[task.issueType] && (
          <Tooltip title={issueTypeConfig[task.issueType].label}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                bgcolor: alpha(issueTypeConfig[task.issueType].color, 0.1),
              }}
            >
              {React.createElement(issueTypeConfig[task.issueType].icon, {
                size: 12,
                style: { color: issueTypeConfig[task.issueType].color },
              })}
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: issueTypeConfig[task.issueType].color,
                  fontWeight: 500,
                }}
              >
                {issueTypeConfig[task.issueType].label}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Category Badge */}
        {task.category && (
          <Chip
            label={task.category}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              bgcolor: alpha(categoryColors[task.category] || '#9ca3af', 0.15),
              color: categoryColors[task.category] || '#9ca3af',
              fontWeight: 500,
              textTransform: 'capitalize',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}

        {/* Blocked Indicator */}
        {isBlocked && (
          <Tooltip title={`Blocked by ${blockedByTasks.length} task(s)`}>
            <Lock size={14} style={{ color: '#f59e0b' }} />
          </Tooltip>
        )}

        {/* Overdue Indicator */}
        {isOverdue && (
          <Tooltip title="Overdue">
            <AlertTriangle size={14} style={{ color: '#ef4444' }} />
          </Tooltip>
        )}

        {/* Dependencies Indicator */}
        {showDependencies && hasDependencies && (
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                {blockedByTasks.length > 0 && (
                  <Typography variant="caption" display="block">
                    Blocked by: {blockedByTasks.map((t) => t.title).join(', ')}
                  </Typography>
                )}
                {blockingTasks.length > 0 && (
                  <Typography variant="caption" display="block">
                    Blocking: {blockingTasks.map((t) => t.title).join(', ')}
                  </Typography>
                )}
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Link2 size={14} style={{ color: '#6366f1' }} />
              <Typography variant="caption" color="primary">
                {blockedByTasks.length + blockingTasks.length}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Subtask Progress */}
        {subtaskProgress && (
          <Tooltip title={`${subtaskProgress.completed}/${subtaskProgress.total} subtasks completed`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
              <LinearProgress
                variant="determinate"
                value={subtaskProgress.percentage}
                sx={{
                  width: 50,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: subtaskProgress.percentage === 100 ? '#16a34a' : '#4F46E5',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatPercent(subtaskProgress.percentage)}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Priority Badge */}
        <PriorityBadge priority={task.priority} size="small" />

        {/* Status Chip */}
        <Chip
          label={getStatusLabel(task.status)}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            bgcolor: `${getStatusColor(task.status)}15`,
            color: getStatusColor(task.status),
            fontWeight: 500,
          }}
        />

        {/* Due Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 70 }}>
          <Clock size={12} style={{ color: '#94a3b8' }} />
          <Typography
            variant="caption"
            sx={{
              color: isOverdue ? 'error.main' : 'text.secondary',
              fontWeight: isOverdue ? 600 : 400,
            }}
          >
            {dayjs(task.dueDate).format('MMM D')}
          </Typography>
        </Box>
      </Box>

      {/* Subtasks */}
      {hasSubtasks && (
        <Collapse in={isExpanded}>
          <Box sx={{ position: 'relative' }}>
            {/* Vertical connector line */}
            <Box
              sx={{
                position: 'absolute',
                left: level * 24 + 36,
                top: 0,
                bottom: 12,
                width: 2,
                bgcolor: 'divider',
              }}
            />
            {subtasks
              .sort((a, b) => a.order - b.order)
              .map((subtask, index) => (
                <Box key={subtask.id} sx={{ position: 'relative' }}>
                  {/* Horizontal connector */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: level * 24 + 36,
                      top: 20,
                      width: 16,
                      height: 2,
                      bgcolor: 'divider',
                    }}
                  />
                  <TaskNode
                    task={subtask}
                    allTasks={allTasks}
                    level={level + 1}
                    expandedNodes={expandedNodes}
                    onToggleExpand={onToggleExpand}
                    showDependencies={showDependencies}
                    onTaskClick={onTaskClick}
                  />
                </Box>
              ))}
          </Box>
        </Collapse>
      )}

      {/* Dependencies Section */}
      {showDependencies && isExpanded && hasDependencies && (
        <Box
          sx={{
            ml: level * 3 + 6,
            p: 1,
            bgcolor: alpha('#6366f1', 0.05),
            borderRadius: 1,
            mb: 1,
          }}
        >
          {blockedByTasks.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                Blocked by:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {blockedByTasks.map((dep) => (
                  <Chip
                    key={dep.id}
                    label={dep.title}
                    size="small"
                    icon={
                      dep.status === TaskStatus.COMPLETED ? (
                        <CheckCircle size={12} />
                      ) : (
                        <Circle size={12} />
                      )
                    }
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: dep.status === TaskStatus.COMPLETED ? '#dcfce7' : '#fee2e2',
                      color: dep.status === TaskStatus.COMPLETED ? '#166534' : '#991b1b',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                    onClick={() => {
                      if (onTaskClick) {
                        onTaskClick(dep.id);
                      } else {
                        navigate(`/tasks?taskId=${dep.id}`);
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {blockingTasks.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                Blocking:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {blockingTasks.map((dep) => (
                  <Chip
                    key={dep.id}
                    label={dep.title}
                    size="small"
                    icon={<ArrowRight size={12} />}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: '#e0e7ff',
                      color: '#3730a3',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                    onClick={() => {
                      if (onTaskClick) {
                        onTaskClick(dep.id);
                      } else {
                        navigate(`/tasks?taskId=${dep.id}`);
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * ProjectNode
 * Renders a project with its child projects and tasks
 */
const ProjectNode: React.FC<ProjectNodeProps> = ({
  project,
  childProjects,
  tasks,
  level,
  expandedNodes,
  onToggleExpand,
  showDependencies,
  onTaskClick,
}) => {
  const navigate = useNavigate();
  const isExpanded = expandedNodes.has(project.id);

  // Get root tasks for this project (tasks without a parent)
  const rootTasks = useMemo(() => {
    return tasks
      .filter((t) => t.projectId === project.id && !t.parentTaskId)
      .sort((a, b) => a.order - b.order);
  }, [tasks, project.id]);

  // Get child projects
  const projectChildren = childProjects.filter((p) => p.parentId === project.id);

  // Calculate project stats
  const stats = useMemo(() => {
    const projectTasks = tasks.filter((t) => t.projectId === project.id);
    const completed = projectTasks.filter((t) => t.status === TaskStatus.COMPLETED).length;
    const inProgress = projectTasks.filter((t) => t.status === TaskStatus.IN_PROGRESS).length;
    const blocked = projectTasks.filter((t) => t.status === TaskStatus.BLOCKED).length;
    const progress = projectTasks.length > 0 ? Math.round((completed / projectTasks.length) * 100) : 0;
    return {
      total: projectTasks.length,
      completed,
      inProgress,
      blocked,
      progress,
    };
  }, [tasks, project.id]);

  const hasChildren = projectChildren.length > 0 || rootTasks.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      onToggleExpand(project.id);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/projects/${project.id}`);
  };

  return (
    <Box>
      {/* Project Row */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          pl: level * 3 + 1.5,
          borderRadius: 1,
          cursor: hasChildren ? 'pointer' : 'default',
          bgcolor: level === 0 ? alpha('#f59e0b', 0.05) : 'transparent',
          '&:hover': {
            bgcolor: level === 0 ? alpha('#f59e0b', 0.1) : 'action.hover',
          },
        }}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(project.id);
            }}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 30 }} />
        )}

        {/* Folder Icon */}
        {isExpanded ? (
          <FolderOpen size={20} style={{ color: '#f59e0b' }} />
        ) : (
          <Folder size={20} style={{ color: '#f59e0b' }} />
        )}

        {/* Project Name */}
        <Typography
          variant="body2"
          fontWeight={600}
          onClick={handleNavigate}
          sx={{
            flex: 1,
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        >
          {project.name}
        </Typography>

        {/* Task Count Badge */}
        <Chip
          icon={<Layers size={12} />}
          label={`${stats.total} tasks`}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.7rem' }}
        />

        {/* Blocked Count */}
        {stats.blocked > 0 && (
          <Chip
            icon={<Lock size={12} />}
            label={stats.blocked}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              bgcolor: '#fef3c7',
              color: '#92400e',
              '& .MuiChip-icon': { color: '#92400e' },
            }}
          />
        )}

        {/* Status Chip */}
        <Chip
          label={project.status}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            textTransform: 'capitalize',
            bgcolor: `${getStatusColor(project.status)}15`,
            color: getStatusColor(project.status),
            fontWeight: 500,
          }}
        />

        {/* Progress */}
        {stats.total > 0 && (
          <Tooltip title={`${stats.completed}/${stats.total} tasks completed`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
              <LinearProgress
                variant="determinate"
                value={stats.progress}
                sx={{
                  width: 60,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: stats.progress === 100 ? '#16a34a' : '#4F46E5',
                  },
                }}
              />
              <Typography variant="caption" fontWeight={500}>
                {formatPercent(stats.progress)}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={isExpanded}>
          {/* Child Projects */}
          {projectChildren.map((child) => (
            <ProjectNode
              key={child.id}
              project={child}
              childProjects={childProjects}
              tasks={tasks}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              showDependencies={showDependencies}
              onTaskClick={onTaskClick}
            />
          ))}

          {/* Root Tasks (tasks without parent) */}
          {rootTasks.map((task) => (
            <TaskNode
              key={task.id}
              task={task}
              allTasks={tasks}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              showDependencies={showDependencies}
              onTaskClick={onTaskClick}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

/**
 * ProjectTreeView
 * Hierarchical view of project structure with multilevel tasks and dependencies
 */
const ProjectTreeView: React.FC<ProjectTreeViewProps> = ({
  project,
  childProjects,
  tasks,
  onTaskClick,
}) => {
  // Track expanded nodes
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Expand root project by default
    return new Set([project.id]);
  });
  const [showDependencies, setShowDependencies] = useState(true);

  // Get all expandable IDs
  const allExpandableIds = useMemo(() => {
    const ids = new Set<string>();
    ids.add(project.id);
    childProjects.forEach((p) => ids.add(p.id));
    tasks.forEach((t) => {
      if (t.subtaskIds && t.subtaskIds.length > 0) {
        ids.add(t.id);
      }
      // Also add tasks that have child tasks via parentTaskId
      const hasChildren = tasks.some((child) => child.parentTaskId === t.id);
      if (hasChildren) {
        ids.add(t.id);
      }
    });
    return ids;
  }, [project.id, childProjects, tasks]);

  const handleToggleExpand = (id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    setExpandedNodes(allExpandableIds);
  };

  const handleCollapseAll = () => {
    setExpandedNodes(new Set([project.id]));
  };

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalTasks = tasks.length;
    const rootTasks = tasks.filter((t) => !t.parentTaskId).length;
    const subtasks = tasks.filter((t) => t.parentTaskId).length;
    const tasksWithDeps = tasks.filter(
      (t) => (t.blockedByTaskIds?.length || 0) > 0 || (t.blockingTaskIds?.length || 0) > 0
    ).length;
    const maxDepth = calculateMaxDepth(tasks);

    return { totalTasks, rootTasks, subtasks, tasksWithDeps, maxDepth };
  }, [tasks]);

  // Calculate max nesting depth
  function calculateMaxDepth(tasks: Task[]): number {
    const depthMap = new Map<string, number>();

    function getDepth(taskId: string): number {
      if (depthMap.has(taskId)) return depthMap.get(taskId)!;

      const task = tasks.find((t) => t.id === taskId);
      if (!task || !task.parentTaskId) {
        depthMap.set(taskId, 0);
        return 0;
      }

      const parentDepth = getDepth(task.parentTaskId);
      const depth = parentDepth + 1;
      depthMap.set(taskId, depth);
      return depth;
    }

    tasks.forEach((t) => getDepth(t.id));
    return Math.max(0, ...Array.from(depthMap.values()));
  }

  return (
    <Card variant="outlined">
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Project Hierarchy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View multilevel task structure with subtasks and dependencies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={showDependencies ? 'contained' : 'outlined'}
            startIcon={<Link2 size={14} />}
            onClick={() => setShowDependencies(!showDependencies)}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Dependencies
          </Button>
          <Tooltip title="Expand All">
            <IconButton size="small" onClick={handleExpandAll}>
              <ChevronsUpDown size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton size="small" onClick={handleCollapseAll}>
              <ChevronsDownUp size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<Layers size={14} />}
          label={`${summary.totalTasks} Total Tasks`}
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<FileText size={14} />}
          label={`${summary.rootTasks} Root Tasks`}
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<GitBranch size={14} />}
          label={`${summary.subtasks} Subtasks`}
          size="small"
          variant="outlined"
          sx={{ '& .MuiChip-icon': { transform: 'rotate(180deg)' } }}
        />
        <Chip
          icon={<Link2 size={14} />}
          label={`${summary.tasksWithDeps} With Dependencies`}
          size="small"
          variant="outlined"
        />
        {summary.maxDepth > 0 && (
          <Chip
            label={`Max Depth: ${summary.maxDepth + 1} levels`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Divider />

      {/* Tree Content */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <ProjectNode
          project={project}
          childProjects={childProjects}
          tasks={tasks}
          level={0}
          expandedNodes={expandedNodes}
          onToggleExpand={handleToggleExpand}
          showDependencies={showDependencies}
          onTaskClick={onTaskClick}
        />
      </Box>

      {/* Empty State */}
      {childProjects.length === 0 && tasks.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Folder size={48} style={{ color: '#94a3b8', marginBottom: 12 }} />
          <Typography color="text.secondary" gutterBottom>
            This project has no sub-projects or tasks yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add tasks to see them in the hierarchy view
          </Typography>
        </Box>
      )}

      {/* Legend */}
      {tasks.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Folder size={14} style={{ color: '#f59e0b' }} />
              <Typography variant="caption" color="text.secondary">Project</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FileText size={14} style={{ color: '#64748b' }} />
              <Typography variant="caption" color="text.secondary">Task</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Layers size={14} style={{ color: '#8b5cf6' }} />
              <Typography variant="caption" color="text.secondary">Task with Subtasks</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Link2 size={14} style={{ color: '#6366f1' }} />
              <Typography variant="caption" color="text.secondary">Has Dependencies</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Lock size={14} style={{ color: '#f59e0b' }} />
              <Typography variant="caption" color="text.secondary">Blocked</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AlertTriangle size={14} style={{ color: '#ef4444' }} />
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Bug size={14} style={{ color: '#dc2626' }} />
              <Typography variant="caption" color="text.secondary">Bug</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Lightbulb size={14} style={{ color: '#16a34a' }} />
              <Typography variant="caption" color="text.secondary">Feature</Typography>
            </Box>
          </Box>
        </>
      )}
    </Card>
  );
};

export default ProjectTreeView;
